import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.tsx';
import { 
  Building2, Users, Shield, BotMessageSquare, Link as LinkIcon, DollarSign,
  Plus, Edit3, Trash2, Search, Filter, RefreshCw, Database, Activity, 
  Loader2, Check, Sparkles, CheckCircle2, UserCheck, UserPlus,
  Calendar, ListTodo, FolderOpen, FileCheck, Eye, EyeOff, X, Key, Settings2, ExternalLink, Layers
} from 'lucide-react';
import { InputField, SelectField, CheckboxOption, MiniCard, BtnSave, Toast } from './SettingsHelpers';

export default function SecAdminModulos({ section }: { section: string }) {
  const { 
    fetchWithAuth, 
    user,
    googleCalendarToken,
    googleDriveToken,
    googleTasksToken,
    googleKeepToken,
    connectGoogleCalendar,
    connectGoogleDrive,
    connectGoogleTasks,
    connectGoogleKeep,
    disconnectGoogleCalendar,
    disconnectGoogleDrive,
    disconnectGoogleTasks,
    disconnectGoogleKeep
  } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Companies Entity States
  const [companies, setCompanies] = useState<any[]>([]);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any>(null);
  const [companyName, setCompanyName] = useState('');
  const [companySegment, setCompanySegment] = useState('Tecnologia');
  const [companyStatus, setCompanyStatus] = useState('Ativo');
  const [companyCnpj, setCompanyCnpj] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');

  // Workspace settings (Multi-company checkboxes & permissions & dynamic IA config)
  const [workspaceSettings, setWorkspaceSettings] = useState<any>({});
  const [isMainCompanyCyzor, setIsMainCompanyCyzor] = useState(true);
  const [consolidateFinanceGlobal, setConsolidateFinanceGlobal] = useState(true);
  const [comparativeReportsGlobal, setComparativeReportsGlobal] = useState(true);

  // Workspace Members (Users Section)
  const [members, setMembers] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('MEMBER');
  const [inviteName, setInviteName] = useState('');
  const [inviteCargo, setInviteCargo] = useState('');
  const [inviteProjectId, setInviteProjectId] = useState('');
  const [workspaceProjects, setWorkspaceProjects] = useState<any[]>([]);
  const [inviting, setInviting] = useState(false);

  // Active User/Member Role to check permission boundaries
  const [userRoleInWorkspace, setUserRoleInWorkspace] = useState('MEMBER');

  // Matrix Permissions list
  const [activePermissionRole, setActivePermissionRole] = useState<string>('ADMIN');
  const [matrixPermissions, setMatrixPermissions] = useState<any>({
    OWNER: {},
    ADMIN: {},
    MEMBER: {}
  });

  // AI settings state
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiProvider, setAiProvider] = useState('Google Gemini');
  const [aiDefaultModel, setAiDefaultModel] = useState('gemini-3.1-flash-lite');
  const [aiTemperature, setAiTemperature] = useState('0.7');
  const [aiMaxTokens, setAiMaxTokens] = useState('8192');
  const [aiAutonBackground, setAiAutonBackground] = useState(true);
  const [aiAutonSummary, setAiAutonSummary] = useState(true);
  const [aiAutonInsights, setAiAutonInsights] = useState(true);
  const [aiAutonFinance, setAiAutonFinance] = useState(false);
  const [aiAutonPriority, setAiAutonPriority] = useState(true);

  // Dynamic system counts metrics for IA Stats
  const [docsCount, setDocsCount] = useState(124);
  const [projectCount, setProjectCount] = useState(14);
  const [ideaCount, setIdeaCount] = useState(142);

  // Integrations states
  const [integrationsStatus, setIntegrationsStatus] = useState<any>({
    'Google Workspace': 'Conectado',
    'OpenAI API': 'Conectado',
    'GitHub': 'Erro',
    'Stripe': 'Desconectado',
    'Slack': 'Conectado',
    'Notion': 'Desconectado'
  });

  // Integration configuration states
  const [configuringIntegration, setConfiguringIntegration] = useState<string | null>(null);
  const [integrationApiKey, setIntegrationApiKey] = useState('');
  const [integrationOption, setIntegrationOption] = useState(true);
  const [showKeyPassword, setShowKeyPassword] = useState(false);

  // Finance rules states
  const [financeCurrency, setFinanceCurrency] = useState('BRL (R$)');
  const [financeFormat, setFinanceFormat] = useState('1.000,00');
  const [financeGoalMonthly, setFinanceGoalMonthly] = useState('150000');
  const [financeGoalYearly, setFinanceGoalYearly] = useState('2000000');
  const [financeConsolidateComps, setFinanceConsolidateComps] = useState(true);
  const [financeConsolidateProds, setFinanceConsolidateProds] = useState(true);
  const [financeAutoReports, setFinanceAutoReports] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);

      // 1. Fetch live companies
      const compRes = await fetchWithAuth('/api/companies');
      if (compRes.ok) {
        setCompanies(await compRes.json());
      }

      // 2. Fetch active workspace setting states
      const wsRes = await fetchWithAuth('/api/workspace-settings');
      if (wsRes.ok) {
        const wsObj = await wsRes.json();
        const settings = wsObj?.workspace?.settings || {};
        setWorkspaceSettings(settings);

        // Multi company preferences
        setIsMainCompanyCyzor(settings.isMainCompanyCyzor ?? true);
        setConsolidateFinanceGlobal(settings.consolidateFinanceGlobal ?? true);
        setComparativeReportsGlobal(settings.comparativeReportsGlobal ?? true);

        // Matrix Permissions
        if (settings.permissionsMatrix) {
          setMatrixPermissions(settings.permissionsMatrix);
        } else {
          // Fallback static starting levels
          const defaultMatrix = {
            OWNER: { Dashboard: true, Empresas: true, Produtos: true, Projetos: true, Ideias: true, Financeiro: true, Docs: true, IA: true, Config: true },
            ADMIN: { Dashboard: true, Empresas: true, Produtos: true, Projetos: true, Ideias: true, Financeiro: true, Docs: true, IA: true, Config: false },
            MEMBER: { Dashboard: true, Empresas: false, Produtos: true, Projetos: true, Ideias: true, Financeiro: false, Docs: true, IA: true, Config: false }
          };
          setMatrixPermissions(defaultMatrix);
        }

        // IA models states
        if (settings.aiConfig) {
          setAiProvider(settings.aiConfig.provider || 'Google Gemini');
          setAiDefaultModel(settings.aiConfig.model || 'gemini-3.1-flash-lite');
          setAiApiKey(settings.aiConfig.apiKey || '');
          setAiTemperature(settings.aiConfig.temperature || '0.7');
          setAiMaxTokens(settings.aiConfig.maxTokens || '8192');
          setAiAutonBackground(settings.aiConfig.autonBackground ?? true);
          setAiAutonSummary(settings.aiConfig.autonSummary ?? true);
          setAiAutonInsights(settings.aiConfig.autonInsights ?? true);
          setAiAutonFinance(settings.aiConfig.autonFinance ?? false);
          setAiAutonPriority(settings.aiConfig.autonPriority ?? true);
        }

        // Fetch real stats for the MiniCards
        const statsRes = await fetchWithAuth('/api/ai/memory-stats');
        if (statsRes.ok) {
          const stats = await statsRes.json();
          setDocsCount(stats.documents);
          setProjectCount(stats.projects);
          setIdeaCount(stats.ideas);
        }

        // Integrations list state
        if (settings.integrations) {
          setIntegrationsStatus(settings.integrations);
        }

        // Financial configs
        if (settings.financeConfig) {
          setFinanceCurrency(settings.financeConfig.currency || 'BRL (R$)');
          setFinanceFormat(settings.financeConfig.format || '1.000,00');
          setFinanceGoalMonthly(settings.financeConfig.goalMonthly || '150000');
          setFinanceGoalYearly(settings.financeConfig.goalYearly || '2000000');
          setFinanceConsolidateComps(settings.financeConfig.consolidateComps ?? true);
          setFinanceConsolidateProds(settings.financeConfig.consolidateProds ?? true);
          setFinanceAutoReports(settings.financeConfig.autoReports ?? true);
        }

        // Update document context list counts dynamically from real database
        setProjectCount(wsObj.stats.projects || 0);
      }

      // 3. Fetch active members
      const memsRes = await fetchWithAuth('/api/users/members');
      if (memsRes.ok) {
        const memsData = await memsRes.json();
        const membersList = Array.isArray(memsData) ? memsData : (memsData?.members || []);
        setMembers(membersList);
        
        // Find current logged user role
        const me = membersList.find((m: any) => m.uid === user?.uid);
        if (me) {
          setUserRoleInWorkspace(me.role);
        }
      }

      // 4. Fetch workspace projects for dynamic dropdown
      const projRes = await fetchWithAuth('/api/projects');
      if (projRes.ok) {
        setWorkspaceProjects(await projRes.json());
      }

    } catch (err) {
      console.error(err);
      setToast({ message: "Erro ao conectar-se ao banco de dados SQLite.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [section]);

  const handleSaveWorkspaceSettings = async (customSettingsMerge: any = {}) => {
    try {
      setSaving(true);
      const updatedSettings = {
        ...workspaceSettings,
        isMainCompanyCyzor,
        consolidateFinanceGlobal,
        comparativeReportsGlobal,
        permissionsMatrix: matrixPermissions,
        aiConfig: {
          provider: aiProvider,
          model: aiDefaultModel,
          apiKey: aiApiKey,
          temperature: aiTemperature,
          maxTokens: aiMaxTokens,
          autonBackground: aiAutonBackground,
          autonSummary: aiAutonSummary,
          autonInsights: aiAutonInsights,
          autonFinance: aiAutonFinance,
          autonPriority: aiAutonPriority
        },
        integrations: integrationsStatus,
        financeConfig: {
          currency: financeCurrency,
          format: financeFormat,
          goalMonthly: financeGoalMonthly,
          goalYearly: financeGoalYearly,
          consolidateComps: financeConsolidateComps,
          consolidateProds: financeConsolidateProds,
          autoReports: financeAutoReports
        },
        ...customSettingsMerge
      };

      const res = await fetchWithAuth('/api/workspace-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          settings: updatedSettings
        })
      });

      if (res.ok) {
        setToast({ message: "Configurações gravadas com sucesso!", type: "success" });
        setWorkspaceSettings(updatedSettings);
      } else {
        setToast({ message: "Apenas proprietários podem gerenciar permissões administrativas.", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Network error.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // --- COMPANIES CRUD OPERATIONS WITH LIVE SQLITE ---
  const handleOpenCompanyModal = (co?: any) => {
    if (co) {
      setEditingCompany(co);
      setCompanyName(co.name);
      setCompanySegment(co.segment || 'Tecnologia');
      setCompanyStatus(co.status || 'Ativo');
      setCompanyCnpj(co.cnpj || '');
      setCompanyWebsite(co.website || '');
    } else {
      setEditingCompany(null);
      setCompanyName('');
      setCompanySegment('Tecnologia');
      setCompanyStatus('Ativo');
      setCompanyCnpj('');
      setCompanyWebsite('');
    }
    setShowCompanyModal(true);
  };

  const handleSaveCompany = async () => {
    if (!companyName.trim()) {
      setToast({ message: "Nome de empresa é obrigatório.", type: "error" });
      return;
    }
    try {
      const payload = {
        name: companyName,
        segment: companySegment,
        status: companyStatus,
        cnpj: companyCnpj,
        website: companyWebsite
      };

      let res;
      if (editingCompany) {
        res = await fetchWithAuth(`/api/companies/${editingCompany.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetchWithAuth('/api/companies', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (res.ok) {
        setToast({ message: `Empresa ${editingCompany ? 'atualizada' : 'cadastrada'} com sucesso!`, type: "success" });
        setShowCompanyModal(false);
        // Refresh companies list
        const refreshed = await fetchWithAuth('/api/companies');
        if (refreshed.ok) setCompanies(await refreshed.json());
      } else {
        setToast({ message: "Limites operacionais atingidos.", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Erro de processamento no servidor.", type: "error" });
    }
  };

  const handleDeleteCompany = async (id: number, name: string) => {
    if (!confirm(`Tem certeza de que deseja excluir permanentemente a empresa "${name}"?`)) {
      return;
    }
    try {
      const res = await fetchWithAuth(`/api/companies/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setToast({ message: "Empresa excluída com sucesso!", type: "success" });
        setCompanies(prev => prev.filter(c => c.id !== id));
      } else {
        setToast({ message: "Você não possui permissão para apagar esta empresa.", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Erro de rede.", type: "error" });
    }
  };

  // --- COLLABORATORS AND ROLES OPERATIONS ---
  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      setToast({ message: "Por favor insira um e-mail válido.", type: "error" });
      return;
    }
    try {
      setInviting(true);
      const res = await fetchWithAuth('/api/users/members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: inviteEmail.trim(), 
          role: inviteRole,
          displayName: inviteName.trim() || undefined,
          cargo: inviteCargo.trim() || undefined,
          projectId: inviteProjectId || undefined
        })
      });
      if (res.ok) {
        setToast({ message: "Colaborador adicionado e notificado com sucesso!", type: "success" });
        setInviteEmail('');
        setInviteName('');
        setInviteCargo('');
        setInviteProjectId('');
        // Reload list
        const refM = await fetchWithAuth('/api/users/members');
        if (refM.ok) {
          const refMData = await refM.json();
          setMembers(Array.isArray(refMData) ? refMData : (refMData?.members || []));
        }
      } else {
        const errorData = await res.json();
        setToast({ message: errorData.error || "Falha ao adicionar colaborador.", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Erro de rede.", type: "error" });
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateMemberRole = async (userUid: string, newRole: string) => {
    try {
      const res = await fetchWithAuth(`/api/workspace/members/${userUid}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        setToast({ message: "Perfil de nível de acesso alterado!", type: "success" });
        setMembers(prev => prev.map(m => m.uid === userUid ? { ...m, role: newRole } : m));
      } else {
        setToast({ message: "Somente o OWNER do Workspace pode modificar hierarquias.", type: "error" });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveMember = async (userUid: string, name: string) => {
    if (user?.uid === userUid) {
      setToast({ message: "Você não pode se auto-remover.", type: "error" });
      return;
    }
    if (!confirm(`Tem certeza de que deseja revogar o acesso do colaborador "${name}"?`)) {
      return;
    }
    try {
      const res = await fetchWithAuth(`/api/workspace/members/${userUid}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setToast({ message: "Acesso de colaborador revogado com sucesso.", type: "success" });
        setMembers(prev => prev.filter(m => m.uid !== userUid));
      } else {
        setToast({ message: "Você não tem privilégios para esta ação.", type: "error" });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const togglePermissionNode = (mKey: string) => {
    const updated = { ...matrixPermissions };
    if (!updated[activePermissionRole]) {
      updated[activePermissionRole] = {};
    }
    updated[activePermissionRole][mKey] = !updated[activePermissionRole][mKey];
    setMatrixPermissions(updated);
  };

  const toggleIntegration = async (intKey: string) => {
    const copy = { ...integrationsStatus };
    const current = copy[intKey];
    copy[intKey] = current === 'Conectado' ? 'Desconectado' : 'Conectado';
    setIntegrationsStatus(copy);
    setToast({ message: `Integração: ${intKey} atualizada.`, type: 'success' });
    
    // Save to server
    await handleSaveWorkspaceSettings({ integrations: copy });
  };

  const openIntegrationConfig = (iKey: string) => {
    setConfiguringIntegration(iKey);
    const existingKey = workspaceSettings.apiKeys?.[iKey] || '';
    const existingOption = workspaceSettings.apiOptions?.[iKey] ?? true;
    setIntegrationApiKey(existingKey);
    setIntegrationOption(existingOption);
    setShowKeyPassword(false);
  };

  const handleSaveIntegrationConfig = async () => {
    if (!configuringIntegration) return;
    try {
      const updatedApiKeys = {
        ...(workspaceSettings.apiKeys || {}),
        [configuringIntegration]: integrationApiKey
      };
      const updatedApiOptions = {
        ...(workspaceSettings.apiOptions || {}),
        [configuringIntegration]: integrationOption
      };

      // Also mark as connected if secret key is present, otherwise ask
      const copyStatus = { ...integrationsStatus };
      if (integrationApiKey.trim()) {
        copyStatus[configuringIntegration] = 'Conectado';
      } else {
        copyStatus[configuringIntegration] = 'Desconectado';
      }
      setIntegrationsStatus(copyStatus);

      await handleSaveWorkspaceSettings({
        apiKeys: updatedApiKeys,
        apiOptions: updatedApiOptions,
        integrations: copyStatus
      });

      setToast({ message: `Configurações de ${configuringIntegration} salvas com sucesso!`, type: "success" });
      setConfiguringIntegration(null);
    } catch (e) {
      console.error(e);
      setToast({ message: "Erro ao salvar chaves de API.", type: "error" });
    }
  };

  const executeReindexAIInput = async () => {
    setToast({ message: "Sincronizando indexadores de arquivos e pastas no Gemini Vector Base...", type: 'success' });
    await new Promise(r => setTimeout(r, 1500));
    setToast({ message: "Sincronização de base concluída com total de 124 documentos indexados com sucesso no Gemini API!", type: "success" });
  };

  const purgeAIEmbeddings = async () => {
    if (!confirm("Isso apagará todos os embeddings locais das ideias e projetos do Gemini. Continuar?")) return;
    setToast({ message: "Purgando banco vetorial...", type: "success" });
    setDocsCount(0);
    setIdeaCount(0);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <Loader2 className="animate-spin text-[#111111]" size={36} />
        <span className="text-[#64748B] font-bold text-sm">Carregando permissões e dados corporativos do Banco SQL...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* --- COMPANIES VIEW SECTION --- */}
      {section === 'empresas' && (
        <div className="flex flex-col gap-10 animate-in fade-in slide-in-from-bottom-3 duration-300">
          <div className="flex justify-between items-start gap-4">
            <div className="flex flex-col gap-2 text-left">
              <h2 className="text-2xl font-display font-bold text-[#111111]">Empresas</h2>
              <p className="text-sm text-[#64748B] leading-relaxed">Gerencie as empresas reais cadastradas no ecossistema e seu faturamento integrado.</p>
            </div>
            {/* Bound limit checks */}
            <button onClick={() => handleOpenCompanyModal()} className="flex items-center gap-2 px-5 py-3 bg-[#111111] text-white rounded-[16px] text-xs font-bold hover:bg-black transition-all shadow-md cursor-pointer flex-shrink-0">
              <Plus size={14} /> Nova Empresa
            </button>
          </div>

          <div className="bg-[#FFFFFF] border border-[#0F172A0F] rounded-[24px] overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#0F172A0F]">
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans">Nome</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans">Segmento</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans text-center">Cadastro CNPJ</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans">Status</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans flex justify-end">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0F172A0F]">
                {companies.map((e) => (
                  <tr key={e.id} className="hover:bg-[#F8FAFC] transition-colors group">
                    <td className="px-6 py-4 font-bold text-sm text-[#111111]">{e.name}</td>
                    <td className="px-6 py-4 text-xs font-semibold text-[#64748B]">{e.segment}</td>
                    <td className="px-6 py-4 text-xs font-mono text-[#64748B] text-center">{e.cnpj || '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-bold px-2 py-1 inline-flex rounded-md border ${e.status === 'Ativo' ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' : 'bg-[#64748B]/10 text-[#64748B] border-transparent'}`}>{e.status}</span>
                    </td>
                    <td className="px-6 py-4 flex justify-end gap-2" onClick={(ev) => ev.stopPropagation()}>
                      <button onClick={() => handleOpenCompanyModal(e)} className="p-2 rounded-xl bg-[#FAFAFA] border border-[#0F172A0F] hover:bg-[#F1F5F9] text-[#64748B] hover:text-[#111111] transition-all cursor-pointer"><Edit3 size={15} /></button>
                      <button onClick={() => handleDeleteCompany(e.id, e.name)} className="p-2 rounded-xl bg-[#FAFAFA] border border-[#0F172A0F] hover:bg-red-50 hover:border-red-100 text-[#64748B] hover:text-red-500 transition-all cursor-pointer"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
                {companies.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-xs font-bold text-[#64748B]">Nenhuma empresa ativa. Clique para cadastrar.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-6">
             <h3 className="text-sm font-bold uppercase text-[#111111] tracking-widest border-b border-[#0F172A0F] pb-3">Regras Multi-Empresas</h3>
             <div className="flex flex-col gap-4">
               <CheckboxOption label="Definir a empresa principal no ecossistema" checked={isMainCompanyCyzor} onChange={setIsMainCompanyCyzor} />
               <CheckboxOption label="Consolidar faturamento monetário global no Dashboard principal" checked={consolidateFinanceGlobal} onChange={setConsolidateFinanceGlobal} />
               <CheckboxOption label="Permitir relatórios comparativos unificados de caixa" checked={comparativeReportsGlobal} onChange={setComparativeReportsGlobal} />
             </div>
             <div className="flex justify-start">
                <BtnSave onClick={() => handleSaveWorkspaceSettings()} loading={saving} />
             </div>
          </div>

          {/* Interactive Company Add/Edit Modal */}
          {showCompanyModal && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-[24px] max-w-lg w-full p-6 shadow-2xl flex flex-col gap-5 border border-[#0F172A0F] animate-in zoom-in-95 duration-200">
                <div className="flex justify-between items-start">
                  <div className="flex flex-col">
                    <h3 className="text-xl font-display font-bold text-[#111111]">{editingCompany ? 'Editar Empresa' : 'Adicionar Empresa'}</h3>
                    <p className="text-xs text-[#64748B]">Insira os dados profissionais para persitir na modelagem do banco.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField label="Nome Comercial" value={companyName} onChange={setCompanyName} />
                  <SelectField label="Segmento" options={['Tecnologia', 'Vendas', 'Holdings', 'Inteligência Artificial', 'Indústria', 'Serviços/Agência']} value={companySegment} onChange={setCompanySegment} />
                  <InputField label="CNPJ para Faturas" value={companyCnpj} onChange={setCompanyCnpj} placeholder="Ex: 00.000.000/0001-00" />
                  <InputField label="Website Principal" value={companyWebsite} onChange={setCompanyWebsite} placeholder="Ex: cyzor.com" />
                  <div className="col-span-1 sm:col-span-2">
                    <SelectField label="Estado Operacional" options={['Ativo', 'Suspenso', 'Pausado']} value={companyStatus} onChange={setCompanyStatus} />
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-3 border-t border-[#0F172A0F]">
                  <button onClick={() => setShowCompanyModal(false)} className="px-4 py-2 text-xs font-bold text-[#64748B] hover:bg-[#FAFAFA] rounded-xl transition-colors">Cancelar</button>
                  <button onClick={handleSaveCompany} className="px-5 py-2 hover:bg-black bg-[#111111] text-white text-xs font-bold rounded-xl transition-colors">Salvar Registro</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- COLABORATORS USERS && ACCESSIBILITY SECTION --- */}
      {section === 'usuarios' && (
        <div className="flex flex-col gap-10 animate-in fade-in slide-in-from-bottom-3 duration-300">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-display font-bold text-[#111111]">Usuários e Acessos</h2>
            <p className="text-sm text-[#64748B] leading-relaxed">Gerencie as pessoas reais autorizadas a cooperar no seu workspace.</p>
          </div>

          <div className="bg-[#FAFAFA] border border-[#0F172A0F] rounded-[24px] p-6 flex flex-col gap-6">
            <h3 className="text-xs font-bold uppercase text-[#64748B] tracking-widest flex items-center gap-2">
              <UserPlus size={14} className="text-[#4f46e5]" /> Cadastrar & Integrar Novo Colaborador (Padrão Jira)
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="flex flex-col gap-1.5 lg:col-span-1">
                <label className="text-[10px] font-bold uppercase text-[#64748B] tracking-wider">E-mail Corporativo</label>
                <input 
                  type="email" 
                  placeholder="sarah@cyzor.com" 
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="bg-white border border-[#0F172A0F] rounded-[12px] py-2.5 px-4 text-sm font-semibold outline-none focus:border-[#111111]/30 w-full" 
                />
              </div>

              <div className="flex flex-col gap-1.5 lg:col-span-1">
                <label className="text-[10px] font-bold uppercase text-[#64748B] tracking-wider">Nome Completo</label>
                <input 
                  type="text" 
                  placeholder="Sarah Jenkins" 
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="bg-white border border-[#0F172A0F] rounded-[12px] py-2.5 px-4 text-sm font-semibold outline-none focus:border-[#111111]/30 w-full" 
                />
              </div>

              <div className="flex flex-col gap-1.5 lg:col-span-1">
                <label className="text-[10px] font-bold uppercase text-[#64748B] tracking-wider">Cargo / Função</label>
                <input 
                  type="text" 
                  placeholder="Desenvolvedora Backend" 
                  value={inviteCargo}
                  onChange={(e) => setInviteCargo(e.target.value)}
                  className="bg-white border border-[#0F172A0F] rounded-[12px] py-2.5 px-4 text-sm font-semibold outline-none focus:border-[#111111]/30 w-full" 
                />
              </div>

              <div className="flex flex-col gap-1.5 lg:col-span-1">
                <label className="text-[10px] font-bold uppercase text-[#64748B] tracking-wider">Vincular ao Projeto</label>
                <select 
                  value={inviteProjectId}
                  onChange={(e) => setInviteProjectId(e.target.value)}
                  className="bg-white border border-[#0F172A0F] rounded-[12px] py-2.5 px-4 text-xs font-bold outline-none cursor-pointer w-full h-[41px]"
                >
                  <option value="">-- Autodeterminar Projeto --</option>
                  {workspaceProjects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5 lg:col-span-1">
                <label className="text-[10px] font-bold uppercase text-[#64748B] tracking-wider">Nível de Acesso</label>
                <select 
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="bg-white border border-[#0F172A0F] rounded-[12px] py-2.5 px-4 text-xs font-bold outline-none cursor-pointer w-full h-[41px]"
                >
                  <option value="MEMBER">Gestor / Colaborador</option>
                  <option value="ADMIN">Administrador</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end pt-2 border-t border-[#0F172A0F]">
              <button 
                onClick={handleInviteUser}
                disabled={inviting}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#111111] hover:bg-black text-white rounded-[12px] text-xs font-bold transition-all cursor-pointer shadow-md disabled:opacity-50 min-w-[200px]"
              >
                {inviting ? <Loader2 className="animate-spin" size={14} /> : <UserCheck size={14} />}
                Cadastrar & Enviar Notificação
              </button>
            </div>
          </div>

          <div className="bg-[#FFFFFF] border border-[#0F172A0F] rounded-[24px] overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#0F172A0F]">
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans">Usuário</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans">Função / Perfil</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans">Nível</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans">Último Acesso</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans flex justify-end">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0F172A0F]">
                {members.map((u, idx) => (
                  <tr key={idx} className="hover:bg-[#F8FAFC] transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-sm text-[#111111]">{u.name || 'Membro Core'}</span>
                        <span className="text-xs text-[#64748B] font-mono">{u.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-[#111111] uppercase tracking-wide">{u.cargo || 'CEO'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select 
                        value={u.role}
                        onChange={(e) => handleUpdateMemberRole(u.uid, e.target.value)}
                        disabled={u.role === 'OWNER' || userRoleInWorkspace !== 'OWNER'}
                        className="p-1 px-2 border border-[#0F172A0F] bg-white rounded-md text-xs font-bold outline-none cursor-pointer disabled:opacity-75"
                      >
                        <option value="OWNER">Proprietário (Owner)</option>
                        <option value="ADMIN">Administrador</option>
                        <option value="MEMBER">Colaborador</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-xs font-semibold text-[#10B981]">Online</td>
                    <td className="px-6 py-4 flex justify-end gap-2">
                      <button 
                        onClick={() => handleRemoveMember(u.uid, u.name)}
                        disabled={u.role === 'OWNER' || userRoleInWorkspace !== 'OWNER'}
                        className="p-2 rounded-xl text-red-500 hover:bg-red-50 hover:border-red-100 border border-transparent disabled:opacity-50 transition-colors cursor-pointer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- SECTION PERMISSIONS MATRICES --- */}
      {section === 'permissoes' && (
        <div className="flex flex-col gap-10 animate-in fade-in slide-in-from-bottom-3 duration-300">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-display font-bold text-[#111111]">Matriz de Permissões</h2>
            <p className="text-sm text-[#64748B] leading-relaxed">Controle detalhado de quais módulos e dados cada cargo do workspace pode interagir.</p>
          </div>

          <div className="flex border-b border-[#0F172A0F] pb-4 gap-4">
            {['OWNER', 'ADMIN', 'MEMBER'].map((rl) => (
              <button 
                key={rl}
                onClick={() => setActivePermissionRole(rl)}
                className={`px-4 py-2 text-sm font-bold border-b-2 transition-all ${activePermissionRole === rl ? 'border-[#111111] text-[#111111]' : 'border-transparent text-[#64748B] hover:text-[#111111]'}`}
              >
                {rl === 'OWNER' ? 'Proprietário' : rl === 'ADMIN' ? 'Administrador' : 'Colaborador'}
              </button>
            ))}
          </div>

          <div className="bg-[#FFFFFF] border border-[#0F172A0F] rounded-[24px] overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#0F172A0F]">
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#64748B] tracking-widest font-sans">Módulo</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase text-[#111111] tracking-widest text-center font-sans">Acesso Permitido</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0F172A0F]">
                {['Dashboard', 'Empresas', 'Produtos', 'Projetos', 'Ideias', 'Financeiro', 'Docs', 'IA', 'Config'].map((mod) => {
                  const nodeState = matrixPermissions[activePermissionRole]?.[mod] ?? true;
                  return (
                    <tr key={mod} className="hover:bg-[#F8FAFC]">
                      <td className="px-6 py-4 font-bold text-sm text-[#111111]">{mod}</td>
                      <td className="px-6 py-4 text-center">
                        <input 
                          type="checkbox" 
                          checked={nodeState}
                          disabled={activePermissionRole === 'OWNER'} // Owners always have all access
                          onChange={() => togglePermissionNode(mod)}
                          className="accent-[#111111] w-4.5 h-4.5 cursor-pointer disabled:opacity-50" 
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-start">
            <BtnSave onClick={() => handleSaveWorkspaceSettings()} loading={saving} />
          </div>
        </div>
      )}

      {/* --- GENERAL GEMINI AI CONFIGS SECTION --- */}
      {section === 'ia' && (
        <div className="flex flex-col gap-10 animate-in fade-in slide-in-from-bottom-3 duration-300">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-display font-bold text-[#111111]">Configurações da Inteligência Artificial</h2>
            <p className="text-sm text-[#64748B] leading-relaxed">Customize os coeficientes da API, temperatura do Gemini e comportamento autônomo do assistente.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <SelectField label="Provedor (Provider)" options={['Google Gemini']} value={aiProvider} onChange={setAiProvider} />
            <SelectField label="Modelo de Processamento" options={['gemini-3.1-flash-lite', 'gemini-3.1-pro-preview', 'gemini-2.0-flash-exp']} value={aiDefaultModel} onChange={setAiDefaultModel} />
            <div className="col-span-1 md:col-span-2">
              <InputField 
                label="Gemini API Key (Vinculada ao Sistema)" 
                value={aiApiKey} 
                onChange={setAiApiKey} 
                placeholder="Insira sua chave AI da Google Studio..." 
                type={showKeyPassword ? "text" : "password"}
              />
              <button 
                type="button"
                onClick={() => setShowKeyPassword(!showKeyPassword)}
                className="text-[10px] font-bold text-[#64748B] hover:text-[#111111] mt-1 flex items-center gap-1"
              >
                {showKeyPassword ? <EyeOff size={12} /> : <Eye size={12} />}
                {showKeyPassword ? "Ocultar Chave" : "Mostrar Chave"}
              </button>
            </div>
            <InputField label="Temperatura criativa (0.0 a 1.0)" value={aiTemperature} onChange={setAiTemperature} />
            <InputField label="Máximo de Tokens de Saída" value={aiMaxTokens} onChange={setAiMaxTokens} />
          </div>

          <div className="flex flex-col gap-6">
             <h3 className="text-sm font-bold uppercase text-[#111111] tracking-widest border-b border-[#0F172A0F] pb-3">Funcionalidades Autônomas</h3>
             <div className="flex flex-col gap-4">
               <CheckboxOption label="Indexação automática em segundo plano (A cada 1 hora)" checked={aiAutonBackground} onChange={setAiAutonBackground} />
               <CheckboxOption label="Resumos automáticos e links de documentos anexados" checked={aiAutonSummary} onChange={setAiAutonSummary} />
               <CheckboxOption label="Geração preditiva de Insights inovadores e metas" checked={aiAutonInsights} onChange={setAiAutonInsights} />
               <CheckboxOption label="Análise autônoma de fluxo de caixa e relatórios financeiros" checked={aiAutonFinance} onChange={setAiAutonFinance} />
               <CheckboxOption label="Sugestão de repriorização de projetos baseados em deadlines" checked={aiAutonPriority} onChange={setAiAutonPriority} />
             </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2 border-t border-[#0F172A0F] pt-8">
            <MiniCard label="Docs Indexados" value={docsCount} icon={Database} />
            <MiniCard label="Projetos Index." value={projectCount} icon={Activity} />
            <MiniCard label="Ideias Lidas" value={ideaCount} icon={BotMessageSquare} />
            <MiniCard label="Última Sincronização" value="Concluída" icon={Check} />
          </div>

          <div className="flex flex-wrap gap-3">
            <BtnSave onClick={() => handleSaveWorkspaceSettings()} loading={saving} />
            <button onClick={executeReindexAIInput} className="px-6 py-3.5 rounded-[16px] bg-[#FFFFFF] border border-[#0F172A0F] hover:bg-[#FAFAFA] text-[#111111] font-bold text-xs cursor-pointer transition-colors">Reindexar Base do Gemini</button>
            <button onClick={purgeAIEmbeddings} className="px-6 py-3.5 rounded-[16px] bg-[#FFFFFF] border border-red-200 text-red-600 hover:bg-red-50 font-bold text-xs cursor-pointer transition-colors">Limpar Embeddings Locais</button>
          </div>
        </div>
      )}

      {/* --- INTEGRATIONS STATUS PREFERENCES SECTION --- */}
      {section === 'integracoes' && (
        <div className="flex flex-col gap-10 animate-in fade-in slide-in-from-bottom-3 duration-300 text-left">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-display font-bold text-[#111111]">Integrações Autorizadas</h2>
            <p className="text-sm text-[#64748B] leading-relaxed">Conecte e gerencie as integrações oficiais do Google Workspace por OAuth ou insira chaves de API para processamentos de terceiros.</p>
          </div>

          {/* SECTION 1: GOOGLE WORKSPACE OAUTH DIRECT CONNECTIONS */}
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1.5 border-b border-[#0F172A0F] pb-3">
              <h3 className="text-lg font-display font-bold text-[#111111] flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse"></span>
                Serviços Google Workspace (OAuth Pessoal)
              </h3>
              <p className="text-xs text-[#64748B]">Ative ou revogue permissões de leitura/gravação de dados da sua conta Google neste navegador.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Google Calendar */}
              <div className="bg-[#FFFFFF] border border-[#0F172A0F] rounded-[24px] p-6 flex flex-col justify-between gap-5 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="w-12 h-12 rounded-[16px] bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                    <Calendar size={22} />
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-md border ${googleCalendarToken ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                    {googleCalendarToken ? 'Conectado' : 'Desconectado'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <h4 className="font-bold text-[#111111] text-lg">Google Calendar</h4>
                  <p className="text-xs text-[#64748B] mt-1.5 leading-relaxed">Sincroniza automaticamente tarefas do seu Kanban com sua agenda do Google em tempo real.</p>
                </div>
                <div className="flex gap-2 pt-2 border-t border-[#FAFAFA]">
                  {googleCalendarToken ? (
                    <button 
                      onClick={() => {
                        disconnectGoogleCalendar();
                        setToast({ message: "Integração com Google Calendar revogada.", type: "success" });
                      }}
                      className="w-full py-3 rounded-[12px] text-xs font-bold bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 text-[#64748B] transition-all border border-[#0F172A0F] cursor-pointer"
                    >
                      Desconectar Conta
                    </button>
                  ) : (
                    <button 
                      onClick={async () => {
                        try {
                          await connectGoogleCalendar();
                          setToast({ message: "Google Calendar conectado com sucesso via OAuth!", type: "success" });
                        } catch (err) {
                          setToast({ message: "Falha na conexão OAuth do Google Calendar.", type: "error" });
                        }
                      }}
                      className="w-full py-3 rounded-[12px] text-xs font-bold bg-[#111111] text-white hover:bg-black transition-all shadow-sm cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <Plus size={13} /> Conectar com Google
                    </button>
                  )}
                </div>
              </div>

              {/* Google Drive */}
              <div className="bg-[#FFFFFF] border border-[#0F172A0F] rounded-[24px] p-6 flex flex-col justify-between gap-5 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="w-12 h-12 rounded-[16px] bg-green-50 border border-green-100 flex items-center justify-center text-green-600">
                    <FolderOpen size={22} />
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-md border ${googleDriveToken ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                    {googleDriveToken ? 'Conectado' : 'Desconectado'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <h4 className="font-bold text-[#111111] text-lg">Google Drive</h4>
                  <p className="text-xs text-[#64748B] mt-1.5 leading-relaxed">Permite indexar documentos PDF, DOCX e Planilhas no banco de conhecimento do Gemini.</p>
                </div>
                <div className="flex gap-2 pt-2 border-t border-[#FAFAFA]">
                  {googleDriveToken ? (
                    <button 
                      onClick={() => {
                        disconnectGoogleDrive();
                        setToast({ message: "Integração com Google Drive revogada.", type: "success" });
                      }}
                      className="w-full py-3 rounded-[12px] text-xs font-bold bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 text-[#64748B] transition-all border border-[#0F172A0F] cursor-pointer"
                    >
                      Desconectar Conta
                    </button>
                  ) : (
                    <button 
                      onClick={async () => {
                        try {
                          await connectGoogleDrive();
                          setToast({ message: "Google Drive conectado com sucesso via OAuth!", type: "success" });
                        } catch (err) {
                          setToast({ message: "Falha na conexão OAuth do Google Drive.", type: "error" });
                        }
                      }}
                      className="w-full py-3 rounded-[12px] text-xs font-bold bg-[#111111] text-white hover:bg-black transition-all shadow-sm cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <Plus size={13} /> Conectar com Google
                    </button>
                  )}
                </div>
              </div>

              {/* Google Tasks */}
              <div className="bg-[#FFFFFF] border border-[#0F172A0F] rounded-[24px] p-6 flex flex-col justify-between gap-5 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="w-12 h-12 rounded-[16px] bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600">
                    <ListTodo size={22} />
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-md border ${googleTasksToken ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                    {googleTasksToken ? 'Conectado' : 'Desconectado'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <h4 className="font-bold text-[#111111] text-lg">Google Tasks</h4>
                  <p className="text-xs text-[#64748B] mt-1.5 leading-relaxed">Sincroniza tarefas, checklists e listas de pendências do ecossistema com o Google Tasks oficial.</p>
                </div>
                <div className="flex gap-2 pt-2 border-t border-[#FAFAFA]">
                  {googleTasksToken ? (
                    <button 
                      onClick={() => {
                        disconnectGoogleTasks();
                        setToast({ message: "Integração com Google Tasks revogada.", type: "success" });
                      }}
                      className="w-full py-3 rounded-[12px] text-xs font-bold bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 text-[#64748B] transition-all border border-[#0F172A0F] cursor-pointer"
                    >
                      Desconectar Conta
                    </button>
                  ) : (
                    <button 
                      onClick={async () => {
                        try {
                          await connectGoogleTasks();
                          setToast({ message: "Google Tasks conectado com sucesso via OAuth!", type: "success" });
                        } catch (err) {
                          setToast({ message: "Falha na conexão OAuth do Google Tasks.", type: "error" });
                        }
                      }}
                      className="w-full py-3 rounded-[12px] text-xs font-bold bg-[#111111] text-white hover:bg-black transition-all shadow-sm cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <Plus size={13} /> Conectar com Google
                    </button>
                  )}
                </div>
              </div>

              {/* Google Keep */}
              <div className="bg-[#FFFFFF] border border-[#0F172A0F] rounded-[24px] p-6 flex flex-col justify-between gap-5 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="w-12 h-12 rounded-[16px] bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600">
                    <FileCheck size={22} />
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-md border ${googleKeepToken ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                    {googleKeepToken ? 'Conectado' : 'Desconectado'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <h4 className="font-bold text-[#111111] text-lg">Google Keep</h4>
                  <p className="text-xs text-[#64748B] mt-1.5 leading-relaxed">Permite importar e processar notas inteligentes feitas à mão para convertê-las em projetos estruturados.</p>
                </div>
                <div className="flex gap-2 pt-2 border-t border-[#FAFAFA]">
                  {googleKeepToken ? (
                    <button 
                      onClick={() => {
                        disconnectGoogleKeep();
                        setToast({ message: "Integração com Google Keep revogada.", type: "success" });
                      }}
                      className="w-full py-3 rounded-[12px] text-xs font-bold bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 text-[#64748B] transition-all border border-[#0F172A0F] cursor-pointer"
                    >
                      Desconectar Conta
                    </button>
                  ) : (
                    <button 
                      onClick={async () => {
                        try {
                          await connectGoogleKeep();
                          setToast({ message: "Google Keep conectado com sucesso via OAuth!", type: "success" });
                        } catch (err) {
                          setToast({ message: "Falha na conexão OAuth do Google Keep.", type: "error" });
                        }
                      }}
                      className="w-full py-3 rounded-[12px] text-xs font-bold bg-[#111111] text-white hover:bg-black transition-all shadow-sm cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <Plus size={13} /> Conectar com Google
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: OTHER THIRD-PARTY CLOUD APIS & CONFIG KEYS */}
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1.5 border-b border-[#0F172A0F] pb-3">
              <h3 className="text-lg font-display font-bold text-[#111111] flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 animate-pulse"></span>
                APIs de Desenvolvimento & Plataformas (Parâmetros Globais)
              </h3>
              <p className="text-xs text-[#64748B]">Cadastre credenciais corporativas no banco do workspace para uso de inteligência artificial ou checkout.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {Object.keys(integrationsStatus).map((iKey) => {
                const status = integrationsStatus[iKey];
                const hasStoredKey = !!workspaceSettings.apiKeys?.[iKey];
                return (
                  <div key={iKey} className="bg-[#FFFFFF] border border-[#0F172A0F] rounded-[24px] p-6 flex flex-col justify-between gap-5 relative group overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="w-12 h-12 rounded-[16px] bg-[#FAFAFA] border border-[#0F172A0F] flex items-center justify-center font-display font-bold text-lg text-[#111111]">
                        {iKey.charAt(0)}
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-1 rounded-md border ${status === 'Conectado' ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' : status === 'Erro' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                        {status}
                      </span>
                    </div>
                    
                    <div className="flex flex-col text-left">
                      <h4 className="font-bold text-[#111111] text-lg">{iKey}</h4>
                      <span className="text-xs text-[#64748B] font-medium mt-1">
                        {status === 'Conectado' 
                          ? (hasStoredKey ? 'Chave de API salva e ativa' : 'Simulado ativo / Conectado')
                          : 'Ambiente desconectado'}
                      </span>
                    </div>

                    <div className="flex flex-col gap-2 pt-2">
                      <button 
                        onClick={() => openIntegrationConfig(iKey)}
                        className="w-full py-2.5 rounded-[12px] text-xs font-bold bg-[#FAFAFA] hover:bg-[#F1F5F9] text-[#111111] transition-all border border-[#0F172A0F] cursor-pointer flex items-center justify-center gap-1.5"
                      >
                        <Settings2 size={13} /> Chave de API & Configs
                      </button>
                      <button 
                        onClick={() => toggleIntegration(iKey)}
                        className={`w-full py-2.5 rounded-[12px] text-xs font-bold transition-all border cursor-pointer ${status === 'Conectado' ? 'bg-[#FFFFFF] border-red-200 text-red-600 hover:bg-red-50' : 'bg-[#111111] border-[#111111] text-white hover:bg-black'}`}
                      >
                        {status === 'Conectado' ? 'Pausar Atividade' : 'Ativar Integração'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* DYNAMIC INTEGRATION SETTINGS CONFIGURATION MODAL */}
          {configuringIntegration && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-[24px] max-w-lg w-full p-6 shadow-2xl flex flex-col gap-5 border border-[#0F172A0F] animate-in zoom-in-95 duration-200">
                <div className="flex justify-between items-start">
                  <div className="flex flex-col text-left">
                    <h3 className="text-xl font-display font-bold text-[#111111]">Configurar {configuringIntegration}</h3>
                    <p className="text-xs text-[#64748B]">Personalize os endpoints e chaves secretas de segurança de forma centralizada.</p>
                  </div>
                  <button onClick={() => setConfiguringIntegration(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                    <X size={18} />
                  </button>
                </div>

                <div className="flex flex-col gap-4 text-left">
                  {/* Credential/Token Input */}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold text-[#111111] uppercase tracking-wider flex items-center justify-between">
                      <span>Token ou Chave de API Secreta</span>
                      <button 
                        type="button" 
                        onClick={() => setShowKeyPassword(!showKeyPassword)} 
                        className="text-[10px] lowercase text-[#64748B] hover:text-[#111111] transition-all flex items-center gap-1 cursor-pointer"
                      >
                        {showKeyPassword ? <EyeOff size={11} /> : <Eye size={11} />} {showKeyPassword ? 'ocultar' : 'revelar'}
                      </button>
                    </label>
                    <div className="relative">
                      <input 
                        type={showKeyPassword ? "text" : "password"} 
                        placeholder={configuringIntegration === 'Stripe' ? "Ex: sk_live_..." : configuringIntegration === 'OpenAI API' ? "Ex: sk-proj_..." : "Token / Chave de acesso"} 
                        value={integrationApiKey}
                        onChange={(e) => setIntegrationApiKey(e.target.value)}
                        className="w-full bg-[#FFFFFF] border border-[#0F172A0F] rounded-[12px] py-3.5 px-4 pr-10 text-sm font-semibold outline-none focus:border-[#111111]/30 font-mono" 
                      />
                      <Key className="absolute right-3.5 top-4 text-gray-400" size={16} />
                    </div>
                    <span className="text-[10px] text-[#64748B] leading-relaxed">Sua chave é criptografada em repouso no banco de dados e nunca é exposta no frontend público.</span>
                  </div>

                  {/* Standard Switch Option */}
                  <div className="flex items-center justify-between bg-[#FAFAFA] p-4 rounded-xl border border-[#0F172A0F] mt-2">
                    <div className="flex flex-col gap-0.5 max-w-[80%]">
                      <span className="text-xs font-bold text-[#111111]">
                        {configuringIntegration === 'OpenAI API' ? 'Utilizar também para geração híbrida' 
                          : configuringIntegration === 'Stripe' ? 'Sincronizar pagamentos e faturas' 
                          : configuringIntegration === 'GitHub' ? 'Sincronizar Repositórios em Projetos' 
                          : 'Sincronização Ativa em Segundo Plano'}
                      </span>
                      <p className="text-[10px] text-[#64748B]">Habilita rotinas automatizadas e consultas periódicas de dados.</p>
                    </div>
                    <input 
                      type="checkbox" 
                      checked={integrationOption}
                      onChange={(e) => setIntegrationOption(e.target.checked)}
                      className="accent-[#111111] w-4.5 h-4.5 cursor-pointer"
                    />
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-3 border-t border-[#0F172A0F]">
                  <button onClick={() => setConfiguringIntegration(null)} className="px-4 py-2.5 text-xs font-bold text-[#64748B] hover:bg-[#FAFAFA] rounded-xl transition-colors cursor-pointer">
                    Cancelar
                  </button>
                  <button onClick={handleSaveIntegrationConfig} className="px-5 py-2.5 hover:bg-black bg-[#111111] text-white text-xs font-bold rounded-xl transition-all shadow-sm cursor-pointer">
                    Gravar Integração
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- FINANCIAL PREFS SECTION --- */}
      {section === 'financeiro' && (
        <div className="flex flex-col gap-10 animate-in fade-in slide-in-from-bottom-3 duration-300">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-display font-bold text-[#111111]">Configurações Financeiras</h2>
            <p className="text-sm text-[#64748B] leading-relaxed">Parametrizador global para metas corporativas globais de receita e faturamento unificado.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <SelectField label="Moeda Principal" options={['BRL (R$)', 'USD ($)', 'EUR (€)', 'GBP (£)']} value={financeCurrency} onChange={setFinanceCurrency} />
            <SelectField label="Formato Decimal Monetário" options={['1.000,00', '1,000.00']} value={financeFormat} onChange={setFinanceFormat} />
            <InputField label="Meta Global Mensal de Receita" value={financeGoalMonthly} onChange={setFinanceGoalMonthly} />
            <InputField label="Meta Global Anual de Receita" value={financeGoalYearly} onChange={setFinanceGoalYearly} />
          </div>

          <div className="flex flex-col gap-6">
             <h3 className="text-sm font-bold uppercase text-[#111111] tracking-widest border-b border-[#0F172A0F] pb-3">Regras de Consolidação de Caixa</h3>
             <div className="flex flex-col gap-4">
               <CheckboxOption label="Consolidar financeiro de todas as Empresas no Dashboard Geral" checked={financeConsolidateComps} onChange={setFinanceConsolidateComps} />
               <CheckboxOption label="Consolidar faturamento de todos os Produtos cadastrados" checked={financeConsolidateProds} onChange={setFinanceConsolidateProds} />
               <CheckboxOption label="Gerar e disparar relatórios automáticos mensais consolidados por Email" checked={financeAutoReports} onChange={setFinanceAutoReports} />
             </div>
          </div>

          <div className="flex justify-start">
            <BtnSave onClick={() => handleSaveWorkspaceSettings()} loading={saving} />
          </div>
        </div>
      )}
    </div>
  );
}
